import { CacheStrategy } from '../types/cache'

interface CacheEntry {
  expires: number
  body: string
  status: number
  statusText: string
  headers: [string, string][]
}

interface FetchCachedOptions extends RequestInit {
  cacheKey?: string
  strategy?: CacheStrategy
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

const TTL_MAP: Record<CacheStrategy, number> = {
  [CacheStrategy.None]: 0,
  [CacheStrategy.Short]: 10 * 1000,
  [CacheStrategy.Default]: 60 * 1000,
  [CacheStrategy.Long]: 60 * 60 * 1000,
}

const cache = new Map<string, CacheEntry>()

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildCacheKey(url: string, options: FetchCachedOptions): string {
  if (options.cacheKey) return options.cacheKey

  const method = (options.method ?? 'GET').toUpperCase()
  const body =
    options.body != null
      ? typeof options.body === 'string'
        ? options.body
        : JSON.stringify(options.body)
      : ''

  return `${method}:${url}:${body}`
}

function purgeExpired(): void {
  const now = Date.now()
  for (const [key, entry] of cache.entries()) {
    if (entry.expires <= now) {
      cache.delete(key)
    }
  }
}

// Run a light purge every 5 minutes to avoid unbounded memory growth
let purgeScheduled = false

function ensurePurgeScheduled(): void {
  if (purgeScheduled) return
  purgeScheduled = true
  setInterval(purgeExpired, 5 * 60 * 1000)
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Manually invalidate one or more cache entries.
 *
 * @example
 * invalidateCache('my-custom-key')
 * invalidateCache('GET:https://api.example.com/data:')
 */
export function invalidateCache(...keys: string[]): void {
  for (const key of keys) {
    cache.delete(key)
  }
}

/**
 * Wipe the entire in-memory cache.
 */
export function clearCache(): void {
  cache.clear()
}

/**
 * A fetch wrapper with TTL-based in-memory caching.
 *
 * - Cache key defaults to `METHOD:URL:BODY` but can be overridden via `cacheKey`.
 * - Non-safe HTTP methods (POST, PUT, DELETE, PATCH…) are never cached unless
 *   an explicit `cacheKey` is provided.
 * - Failed responses (non-2xx) are never cached.
 * - Expired entries are purged periodically to prevent memory leaks.
 *
 * @example
 * // Basic usage
 * const res = await fetchCached('https://api.example.com/data')
 *
 * // Custom strategy
 * const res = await fetchCached('https://api.example.com/data', { strategy: CacheStrategy.Long })
 *
 * // Custom cache key (useful for POST requests you intentionally want to cache)
 * const res = await fetchCached('https://api.example.com/data', {
 *   method: 'POST',
 *   body: JSON.stringify({ id: 1 }),
 *   cacheKey: 'my-post-key',
 *   strategy: CacheStrategy.Short
 * })
 */
export async function fetchCached(
  url: string,
  options: FetchCachedOptions = {},
): Promise<Response> {
  ensurePurgeScheduled()

  const {
    strategy = CacheStrategy.Default,
    cacheKey,
    ...fetchOptions
  } = options
  const method = (fetchOptions.method ?? 'GET').toUpperCase()
  const now = Date.now()

  const shouldCache =
    strategy !== CacheStrategy.None &&
    (SAFE_METHODS.has(method) || cacheKey != null)

  if (shouldCache) {
    const key = buildCacheKey(url, options)
    const cached = cache.get(key)

    if (cached && cached.expires > now) {
      return new Response(cached.body, {
        status: cached.status,
        statusText: cached.statusText,
        headers: new Headers(cached.headers),
      })
    }
  }

  const response = await fetch(url, fetchOptions)

  if (!shouldCache || !response.ok) {
    return response
  }

  const body = await response.text()
  const key = buildCacheKey(url, options)

  cache.set(key, {
    expires: now + TTL_MAP[strategy],
    body,
    status: response.status,
    statusText: response.statusText,
    headers: Array.from(response.headers.entries()),
  })

  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  })
}
